import { useAtomValue, useSetAtom } from "jotai";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { SettingsResponse } from "@go-novus/admin-api";

import {
  markSettingsStaleAtom,
  pickElementAtom,
  readPickElementFailureMessage,
  readPickEligibility,
  readPickOutcome,
  settingsAtom,
  type PickEligibility,
  type ProactiveRuleDraft,
} from "#/features/settings";
import * as m from "#/paraglide/messages";

import {
  isDraftDirty,
  readDraft,
  type SettingsDraft,
} from "../lib/settings-draft";

/**
 * 拾取会话的对外提示：applied 是写回值已进表单，conflict 是写回值
 * 到达但表单有未保存改动，gone 是返回后发现规则被删或已改类型。
 */
export type PickNotice =
  | { kind: "applied"; ruleId: string }
  | { kind: "conflict"; ruleId: string; selector: string }
  | { kind: "gone"; ruleId: string };

/**
 * 拾取会话：issuing 空白页已开在等令牌，open 店面页可写回，canceling
 * 正在收尾（旧令牌作废、重读确认没落笔），cancel_failed 是收尾没能确认——
 * 保持锁定、留着重试。任何一段没走完，保存都锁住。
 */
export interface PickSession {
  phase: "cancel_failed" | "canceling" | "issuing" | "open";
  ruleId: string;
}

/** 某条规则的拾取签发失败。 */
export interface PickFailure {
  message: string;
  ruleId: string;
}

/** 页面传给规则区的拾取控制面：资格判断、会话状态、失败提示与回调。 */
export interface ElementPickControl {
  eligibility: (rule: ProactiveRuleDraft) => PickEligibility;
  failure: PickFailure | null;
  notice: PickNotice | null;
  /** 未结清的拾取会话；非 null 时所有拾取入口与整页保存都锁住。 */
  session: PickSession | null;
  themeEditorUrl: string | undefined;
  onCancelPick: () => void;
  onDismissNotice: () => void;
  onPick: (ruleId: string) => void;
  onResolveConflict: (resolution: "keep" | "use") => void;
}

export interface UseElementPickOptions {
  /** 草稿对照的已保存配置，由外层表单的保存动作一并推进。 */
  baseline: SettingsResponse;
  draft: SettingsDraft;
  /** 只声明用到的面：整体重置到一份草稿、回写规则数组。 */
  form: {
    reset: (values: SettingsDraft) => void;
    setFieldValue: (
      name: "proactiveRules",
      value: ProactiveRuleDraft[],
    ) => void;
  };
  logoDraftPending: boolean;
  /** 原子查询最新读到的已保存配置。 */
  saved: SettingsResponse;
  setBaseline: (next: SettingsResponse) => void;
}

export interface UseElementPickResult {
  /** 未处理的写回冲突，期间禁止保存。 */
  conflictPending: boolean;
  control: ElementPickControl;
  /** 签发/编辑等动作后清掉拾取相关的行内提示。 */
  dismissMessages: () => void;
  /** 是否存在未结清的拾取会话（签发中/已打开/收尾中），期间禁止保存。 */
  sessionActive: boolean;
}

/**
 * 元素拾取会话：点击时先开一张空白页拿到真句柄（被拦截才返回 null），
 * 签发完成只剩导航一步；会话有 issuing/open/canceling 三段，任何一段
 * 没结清前保存都锁住，离开页面、取消、规则失效都会先把会话收干净。
 *
 * 已保存配置的变化在渲染期对齐基线（守卫式渲染期 setState 是官方对
 * 「props 变了要调状态」的推荐写法，本仓库的 eslint 禁掉 effect 内
 * setState）；拾取结果也在同一分支里判定。
 */
export function useElementPick({
  baseline,
  draft,
  form,
  logoDraftPending,
  saved,
  setBaseline,
}: UseElementPickOptions): UseElementPickResult {
  const pickElement = useAtomValue(pickElementAtom);
  const markSettingsStale = useSetAtom(markSettingsStaleAtom);
  const settingsQuery = useAtomValue(settingsAtom);
  const [session, setSession] = useState<PickSession | null>(null);
  const [notice, setNotice] = useState<PickNotice | null>(null);
  const [failure, setFailure] = useState<PickFailure | null>(null);
  // 新标签句柄：空白页与已导航的店面页都算后台开出来的，会话收尾
  // 或页面下台时一律由后台收掉。
  const tabRef = useRef<Window | null>(null);
  // 异步回调持有的是定义时的闭包，「这段会话还作不作数」比对这份快照。
  // baseline/execute/refetch 每次提交同步刷新；session 字段只在会话
  // 变迁点同步直写——React 的提交可能落在请求返回之后（effect 和原生
  // 事件触发的收尾不享受离散事件同步冲刷），靠提交同步会出现
  // 「回调先到、会话还没立起来」被误判作废、canceling 卡死的窗口。
  // 渲染期回显分支清 state 不回写这里，墓碑值安全：守卫全按对象身份
  // 比对，旧会话对象永远不等于新对象。
  const latestRef = useRef({
    baseline,
    execute: pickElement.execute,
    refetch: settingsQuery.refetch,
    session,
  });
  // 本次挂载有没有开过拾取会话：卸载时据此把配置标过期——结清的重读
  // 到下次挂载之间仍可能落库一笔写回，回来必须重读而不是吃缓存。
  const hadSessionRef = useRef(false);
  useLayoutEffect(() => {
    latestRef.current.baseline = baseline;
    latestRef.current.execute = pickElement.execute;
    latestRef.current.refetch = settingsQuery.refetch;
    if (session !== null) hadSessionRef.current = true;
  });

  const draftDirty = isDraftDirty(draft, baseline) || logoDraftPending;

  if (saved !== baseline && saved.updatedAt !== baseline.updatedAt) {
    setBaseline(saved);

    if (session !== null) {
      const outcome = readPickOutcome(session.ruleId, baseline, saved);
      if (outcome.kind !== "unchanged") {
        setSession(null);
        if (outcome.kind === "gone" || !draftDirty) {
          setNotice(
            outcome.kind === "gone"
              ? { kind: "gone", ruleId: session.ruleId }
              : { kind: "applied", ruleId: session.ruleId },
          );
        } else {
          // 草稿脏：行还在且仍是元素可见才挂得住冲突按钮，否则按失效报。
          const draftRule = draft.proactiveRules.find(
            (rule) => rule.id === session.ruleId,
          );
          setNotice(
            draftRule?.trigger === "element_visible"
              ? {
                  kind: "conflict",
                  ruleId: session.ruleId,
                  selector: outcome.selector,
                }
              : { kind: "gone", ruleId: session.ruleId },
          );
        }
      }
    }

    if (!draftDirty) form.reset(readDraft(saved));
  }

  /**
   * 结束一段会话：关掉自己开的标签、同规则重签把旧令牌顶掉（签发即删除
   * 同租户同规则的旧令牌），再重读一次配置——收尾前已落库的写回仍按
   * 正常回显分支提示。只有「确认没落笔 + 写路已死」才解锁保存；
   * 重签失败或重读拿不到数都转入 cancel_failed，留着重试，不把失败当成功。
   */
  const beginCancel = useCallback((ending: PickSession): void => {
    const held = tabRef.current;
    tabRef.current = null;
    held?.close();
    const canceling: PickSession = {
      phase: "canceling",
      ruleId: ending.ruleId,
    };
    const fail = () => {
      if (latestRef.current.session === canceling) {
        const failed: PickSession = {
          phase: "cancel_failed",
          ruleId: ending.ruleId,
        };
        latestRef.current.session = failed;
        setSession(failed);
      }
    };
    // 同步先把会话立起来：接下来的请求返回可能比 React 提交还早。
    latestRef.current.session = canceling;
    setSession(canceling);
    const { execute, refetch } = latestRef.current;
    void execute(ending.ruleId).then((result) => {
      if (latestRef.current.session !== canceling) return;
      // 重签成功旧令牌已死；4xx 拒绝说明规则在服务端已删或改型，
      // 令牌那条写路本身也死了。网络类失败时旧令牌可能还活着，不能放锁。
      const writePathDead =
        result.isOk() ||
        (result.error.code === "backend.rejected" && !result.error.retryable);
      void refetch().then((refreshed) => {
        if (latestRef.current.session !== canceling) return;
        // 重读失败或只回旧缓存：有没有写回说不清，不能当作已确认。
        const observed =
          refreshed.isError || refreshed.data === undefined
            ? null
            : readPickOutcome(
                ending.ruleId,
                latestRef.current.baseline,
                refreshed.data,
              );
        if (observed === null) {
          fail();
          return;
        }
        // 落库的写回或规则失效交给渲染期回显分支收尾，这里只管「没落笔」。
        if (observed.kind === "unchanged") {
          if (writePathDead) {
            latestRef.current.session = null;
            setSession(null);
          } else {
            fail();
          }
        }
      }, fail);
    }, fail);
  }, []);

  // 焦点回后台就重读：写回与「店面标签被商家关掉」都靠这一刻发现。
  // 全局 focus refetch 受 staleTime 限制可能不动，这里不管新鲜度。
  useEffect(() => {
    if (session === null) return;
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      const held = tabRef.current;
      if (held !== null && held.closed && session.phase !== "canceling") {
        beginCancel(session);
        return;
      }
      void latestRef.current.refetch();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [session, beginCancel]);

  // 页面下台视同放弃这次拾取：自己开的标签收掉、绑定的令牌重签顶掉、
  // 配置标过期（回来挂载时重读，不错过期间落库的写回）；先把快照里的
  // 会话标死，晚到的签发回调落地时不再动作。内部导航已被离开守卫拦到
  // 会话结清之后才放行，这里兜的是强杀与卸载类不可等的路径。
  useEffect(() => {
    return () => {
      const pending = latestRef.current.session;
      latestRef.current = { ...latestRef.current, session: null };
      const held = tabRef.current;
      tabRef.current = null;
      held?.close();
      if (pending !== null) {
        void latestRef.current.execute(pending.ruleId);
      }
      if (pending !== null || hadSessionRef.current) {
        void markSettingsStale();
      }
    };
  }, [markSettingsStale]);

  const dismissMessages = () => {
    // 冲突是商家还没拍板的决定：随手编辑、再开一次拾取都不算处理过，
    // 只有行内的两个明确动作和置顶提示的 Dismiss 能收掉它。
    setNotice((current) => (current?.kind === "conflict" ? current : null));
    setFailure(null);
  };

  return {
    conflictPending: notice?.kind === "conflict",
    control: {
      eligibility: (rule) => readPickEligibility(rule, baseline, draftDirty),
      failure,
      notice,
      onCancelPick: () => {
        if (session === null || session.phase === "canceling") return;
        beginCancel(session);
      },
      onDismissNotice: () => {
        setNotice(null);
      },
      onPick: (ruleId) => {
        if (session !== null) return;
        dismissMessages();
        // 在点击的用户激活窗口里先开空白页：拿到的是真句柄（被浏览器拦截
        // 才返回 null），令牌签好只剩导航一步——既不会再触发弹窗拦截，
        // 也不会被 noopener 恒 null 的返回值误判成失败。
        const tab = window.open("about:blank", "_blank");
        if (tab === null) {
          setFailure({
            message: m.settings_proactive_pick_failure_popup(),
            ruleId,
          });
          return;
        }
        // 与 noopener 等价的断链：店面脚本够不到后台页的 WindowProxy，
        // 但后台保得住句柄（closed / location.replace / close）。
        tab.opener = null;
        tabRef.current = tab;
        const issuing: PickSession = { phase: "issuing", ruleId };
        latestRef.current.session = issuing;
        setSession(issuing);
        void pickElement.execute(ruleId).then((result) => {
          // 会话被取代（取消、结果先到、离开页面）后到达的响应直接作废，
          // 没人要的空白页顺手关掉。
          if (latestRef.current.session !== issuing) {
            tab.close();
            return;
          }
          if (result.isErr()) {
            tab.close();
            tabRef.current = null;
            latestRef.current.session = null;
            setSession(null);
            setFailure({
              message:
                readPickElementFailureMessage(result.error) ??
                m.settings_proactive_pick_failure_unexpected(),
              ruleId,
            });
            return;
          }
          if (tab.closed) {
            // 商家先把空白页关了：按取消走完令牌作废与读回结算。
            beginCancel(issuing);
            return;
          }
          tab.location.replace(result.value.url);
          const open: PickSession = { phase: "open", ruleId };
          latestRef.current.session = open;
          setSession(open);
        });
      },
      onResolveConflict: (resolution) => {
        if (notice?.kind !== "conflict") return;
        if (resolution === "use") {
          form.setFieldValue(
            "proactiveRules",
            draft.proactiveRules.map((rule) =>
              rule.id === notice.ruleId
                ? { ...rule, selector: notice.selector }
                : rule,
            ),
          );
        }
        // 保留修改 = 草稿原样胜出；选采用且行已不在也只会落空，
        // 两种情况提示都收掉，保存恢复可用。
        setNotice(null);
      },
      session,
      themeEditorUrl: saved.themeEditorUrl,
    },
    dismissMessages,
    sessionActive: session !== null,
  };
}
